import { lazy, Suspense, useLayoutEffect } from "react";
import { useAuthStore } from "../auth/auth_store";
import { useSkinSlot } from "../theme/ThemeProvider";
import { EntrySettingsSheet } from "./EntrySettingsSheet";
import { RouteFallback } from "./RouteFallback";

// Route-split the two heavy post-login arms (measured: lazying
// Game + Lobby sheds ~236KB raw off the Login cold-load, which is
// download-bound on mobile Slow-4G). Both are NAMED exports, so we
// adapt the `{ default }` contract `React.lazy` expects. Login stays
// EAGER below — it is the first-paint critical route and rides the
// already-eager i18n / ICU runtime, so deferring its chunk would
// only add a round-trip to the cold path it already blocks on.
const LobbyWiring = lazy(() =>
  import("./LobbyWiring").then((m) => ({ default: m.LobbyWiring })),
);
const Game = lazy(() =>
  import("./Game/Game").then((m) => ({ default: m.Game })),
);

function AuthGateContent(): JSX.Element {
  const LoginSlot = useSkinSlot("Login");
  // Phase-driven branch. The atomic invariant
  // (`phase === "logged_out"` ⟺ `signingKey === null` ⟺
  // `tableIdHex === null`) means each arm is unambiguous; the
  // auth slice cannot land in a partial state mid-render because
  // every transition runs through a single Zustand `set`.
  const phase = useAuthStore((s) => s.phase);
  // Every screen starts at its top. The page is one document, so the
  // scroll a player left the lobby at — down in the table list, where
  // they picked a table — carried into the game, and on a phone the
  // game opened with its top bar pushed up under the address bar.
  const tableIdHex = useAuthStore((s) => s.tableIdHex);
  const screen =
    phase === "logged_out" || phase === "lobby" ? phase : `table:${tableIdHex}`;
  useLayoutEffect(() => {
    if (window.scrollY !== 0) window.scrollTo(0, 0);
  }, [screen]);
  // Boot session-validity gate. A restored session arms
  // `bootProbePending`; `main.tsx`'s probe clears it on a live
  // session (and seeds the wallet) OR routes a swept session through
  // the logout cascade (→ `phase === "logged_out"`, the LoginScreen
  // arm below). Holding the lobby behind the neutral RouteFallback
  // until the probe resolves means a revoked-session refresh never
  // paints the lobby's alarming "예약된 좌석" banner + "$0" wallet
  // before the logout lands — the surface goes straight from the
  // loading shell to the LoginScreen.
  const bootProbePending = useAuthStore((s) => s.bootProbePending);
  if (phase === "logged_out") {
    return (
      <>
        <LoginSlot />
        <EntrySettingsSheet />
      </>
    );
  }
  if (phase === "lobby" && bootProbePending) {
    return <RouteFallback variant="lobby" />;
  }
  // Per-arm Suspense — scoped to the lobby + game arms individually
  // (NOT the whole phase switch, NOT the Login arm) so a Game chunk
  // fetch can never unmount the active sibling surface. The fallback
  // reproduces the loaded arm's `<main id="main-content">` landmark +
  // shell so the skip-to-content target stays mounted and there is
  // no white flash / layout shift during the chunk fetch.
  if (phase === "lobby") {
    return (
      <>
        <Suspense fallback={<RouteFallback variant="lobby" />}>
          <LobbyWiring />
        </Suspense>
        <EntrySettingsSheet />
      </>
    );
  }
  // Both player and spectator modes mount the same `<Game />` shell;
  // the component reads `auth_store.mode` + `game_store.mode` to
  // decide whether to mount the ActionBar / SettledSummary CTA /
  // hole-card derive effect.
  return (
    <Suspense fallback={<RouteFallback variant="game" />}>
      <Game />
    </Suspense>
  );
}

export { AuthGateContent };
